import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Inbox, Presentation, Mail, LogOut, RefreshCw, Trash2, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AdminMail from "@/pages/AdminMail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("nb-NO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const AdminDashboard = () => {
  const { user, logout, authHeader } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inbox");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API}/admin/messages`, { headers: authHeader() })
      .then((r) => setMessages(r.data))
      .catch((e) => {
        if (e.response?.status === 401) {
          logout();
          navigate("/admin");
        } else {
          toast.error("Kunne ikke hente henvendelser");
        }
      })
      .finally(() => setLoading(false));
  }, [authHeader, logout, navigate]);

  useEffect(() => {
    if (user === false) {
      navigate("/admin");
      return;
    }
    if (user) load();
  }, [user, load, navigate]);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/messages/${id}`, { headers: authHeader() });
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Henvendelse slettet");
    } catch {
      toast.error("Kunne ikke slette");
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-sky-600" size={28} />
      </div>
    );
  }

  const tabs = [
    { id: "inbox", label: "Innboks", icon: Inbox, testid: "admin-tab-inbox", count: messages.length },
    { id: "presentations", label: "Presentasjoner", icon: Presentation, testid: "admin-tab-presentations" },
    { id: "email", label: "E-post", icon: Mail, testid: "admin-tab-email" },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <img src="/logo-mark.png" alt="Norgesconsult logo" className="h-10 w-auto object-contain" />
            <span className="font-display text-base font-bold uppercase tracking-[0.24em] text-slate-900">
              Norgesconsult <span className="text-sky-700">Admin</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-slate-500 sm:block" data-testid="admin-user-email">{user.email}</span>
            <button
              onClick={() => { logout(); navigate("/"); }}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-red-300 hover:text-red-600"
            >
              <LogOut size={15} />
              Logg ut
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
        <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={t.testid}
                className={`inline-flex items-center gap-2 rounded-md border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                  active
                    ? "border-sky-600 bg-sky-600 text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:border-sky-600 hover:text-sky-700"
                }`}
              >
                <Icon size={16} />
                {t.label}
                {t.count != null && (
                  <span className={`rounded-full px-2 py-0.5 text-xs ${active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="admin-inbox-count">
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {tab === "inbox" && (
          <div className="mt-8" data-testid="admin-inbox-panel">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-slate-900">Henvendelser fra nettsiden</h2>
              <button
                onClick={load}
                data-testid="admin-inbox-refresh"
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-sky-600 hover:text-sky-700"
              >
                <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
                Oppdater
              </button>
            </div>

            {loading ? (
              <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-sky-600" size={26} /></div>
            ) : messages.length === 0 ? (
              <div className="mt-8 rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="admin-inbox-empty">
                <Inbox size={32} className="mx-auto text-slate-300" />
                <p className="mt-3 text-sm text-slate-500">Ingen henvendelser ennå. Nye meldinger fra kontaktskjemaet dukker opp her.</p>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                {messages.map((m) => (
                  <div key={m.id} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid={`admin-message-${m.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-base font-bold text-slate-900">{m.navn}{m.firma ? ` · ${m.firma}` : ""}</p>
                        <p className="mt-0.5 font-mono text-xs text-slate-400">{fmtDate(m.opprettet)}</p>
                      </div>
                      <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-800">{m.tjeneste}</span>
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-slate-700">{m.melding}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-slate-100 pt-4 text-sm">
                      <a href={`mailto:${m.epost}`} className="font-medium text-sky-700 hover:underline" data-testid={`admin-message-email-${m.id}`}>{m.epost}</a>
                      {m.telefon && <span className="text-slate-500">{m.telefon}</span>}
                      <button
                        onClick={() => remove(m.id)}
                        data-testid={`admin-message-delete-${m.id}`}
                        className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 transition-colors hover:text-red-600"
                      >
                        <Trash2 size={14} />
                        Slett
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "presentations" && (
          <div className="mt-8" data-testid="admin-presentations-panel">
            <div className="rounded-lg border border-slate-200 bg-white p-10 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-6">
                <div className="max-w-xl">
                  <div className="flex items-center gap-3">
                    <div className="rounded-md border border-sky-100 bg-sky-50 p-3 text-sky-700">
                      <Presentation size={22} />
                    </div>
                    <h2 className="font-display text-xl font-bold text-slate-900">Firmapresentasjon</h2>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600">
                    En ferdig Norgesconsult-presentasjon som kjøres fullskjerm direkte fra nettsiden — praktisk
                    i kundemøter og salgssituasjoner. 11 lysbilder med tjenester, prosess, samarbeidsmodeller,
                    referanseprosjekter, ledelse og kontaktinfo.
                  </p>
                  <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
                    <li className="flex gap-2"><span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-sky-600" />Naviger med piltastene eller knappene nederst</li>
                    <li className="flex gap-2"><span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-sky-600" />Trykk F for fullskjerm</li>
                    <li className="flex gap-2"><span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-sky-600" />Direktelenke: /presentasjon — åpnes på hvilken som helst PC</li>
                  </ul>
                </div>
                <a
                  href="/presentasjon"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="admin-open-presentation"
                  className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-sky-700"
                >
                  <Presentation size={16} />
                  Åpne presentasjonen
                </a>
              </div>
            </div>
          </div>
        )}

        {tab === "email" && (
          <div className="mt-8" data-testid="admin-email-panel">
            <AdminMail />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
